import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Wrench, CheckCircle2, Trash2, Pencil, RefreshCcw, ChevronDown, Package, Archive } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import SettingsDialog from "@/components/dialogs/SettingsDialog";
import FeedbackDialog from "@/components/dialogs/FeedbackDialog";
import MaintenanceCompleteDialog from "@/components/dialogs/MaintenanceCompleteDialog";
import MaintenanceItemDialog from "@/components/dialogs/MaintenanceItemDialog";
import ComponentDialog from "@/components/dialogs/ComponentDialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MaintenanceAPI, VehiclesAPI, SettingsAPI, ComponentsAPI } from "@/lib/api";
import { symbolFor } from "@/lib/currency";
import useConfirm from "@/lib/useConfirm";
import Footer from "@/components/Footer";
import WhatsNewDialog from "@/components/dialogs/WhatsNewDialog";
import { componentTypeMeta, COMPONENT_STATUS_META, TONE_COLORS, usageBreakdown } from "@/lib/componentTypes";
import ComponentDetailSheet from "@/components/ComponentDetailSheet";
import logger from "@/lib/logger";
import {
  CATEGORY_META,
  categoryIcon,
  statusMeta,
  formatRemaining,
  formatInterval,
  sortByUrgency,
} from "@/lib/maintenanceMeta";
import { fromKm, unitLabel } from "@/lib/units";

/**
 * Full Maintenance management page.
 *
 * URL supports `?vehicle_id=<id>` so the Vehicle Detail sheet and the
 * Dashboard status card can deep-link into a filtered view.
 */
export default function Maintenance() {
  const location = useLocation();
  const [vehicles, setVehicles] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Lazy init from the URL so a `?vehicle_id=…` deep-link never triggers an
  // initial "all" fetch — that fetch was racing with the filtered one and
  // sometimes clobbering the results (auto-seeding across all vehicles is
  // slower than the single-vehicle query).
  const [selectedVehicleId, setSelectedVehicleId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("vehicle_id") || "all";
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [unit, setUnit] = useState(() => localStorage.getItem("mileage_unit") || "km");
  const [settings, setSettings] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [completingItem, setCompletingItem] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Components tab state — the physical wearables (tires, brake pads, wipers,
  // 12V battery). Stored in a separate collection with its own lifecycle
  // (active / stored / archived) so it stays out of the service schedule's
  // countdown logic. The Maintenance page hosts both views under a tab bar.
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "components" ? "components" : "schedule";
  });
  const [components, setComponents] = useState([]);
  const [componentStatusFilter, setComponentStatusFilter] = useState("all");
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  // Component detail sheet — opened when a row is tapped. Keeps the row
  // itself minimal (no dropdown / no action row); every lifecycle action
  // lives inside the sheet.
  const [viewingComponent, setViewingComponent] = useState(null);

  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    localStorage.setItem("mileage_unit", unit);
  }, [unit]);

  // Also honour URL changes AFTER mount (user navigating between vehicles
  // via in-app links). The lazy state initializer above handles the mount
  // case; this effect handles subsequent updates.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vid = params.get("vehicle_id");
    setSelectedVehicleId(vid || "all");
  }, [location.search]);

  const currencySymbol = settings?.currency_symbol || symbolFor(settings?.currency || "USD");

  // Monotonic request counter — only the newest in-flight response wins.
  // Prevents an "all" fetch (slow, because it auto-seeds every vehicle) from
  // stomping a fast filtered fetch when the user hops between vehicles.
  const requestSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const [vs, its] = await Promise.all([
        VehiclesAPI.list(),
        MaintenanceAPI.list(
          selectedVehicleId !== "all" ? { vehicle_id: selectedVehicleId } : undefined,
        ),
      ]);
      if (seq !== requestSeqRef.current) return; // A newer request has already taken over.
      setVehicles(vs);
      setItems(its);
    } catch (err) {
      logger.error("maintenance-load-failed", err?.message || String(err));
      if (seq === requestSeqRef.current) toast.error("Failed to load maintenance");
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [selectedVehicleId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    SettingsAPI.get().then(setSettings).catch(() => {});
  }, []);

  const activeVehicles = useMemo(() => vehicles.filter((v) => !v.archived), [vehicles]);
  const vehicleById = useMemo(() => {
    const m = {};
    vehicles.forEach((v) => (m[v.id] = v));
    return m;
  }, [vehicles]);

  const filteredItems = useMemo(() => {
    let out = items;
    if (statusFilter !== "all") out = out.filter((it) => it.status === statusFilter);
    return sortByUrgency(out);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const c = { overdue: 0, due_soon: 0, healthy: 0, unknown: 0, total: items.length };
    for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [items]);

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: "Delete maintenance item?",
      description: `“${item.name}” will be removed along with its service history. This can't be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await MaintenanceAPI.remove(item.id);
      toast.success("Item removed");
      load();
    } catch (err) {
      logger.error("maintenance-delete-failed", err?.message || String(err));
      toast.error("Delete failed");
    }
  };

  const handleReseed = async () => {
    if (selectedVehicleId === "all") return;
    const ok = await confirm({
      title: "Restore default services?",
      description:
        "Adds any missing standard maintenance items back into this vehicle's list. Existing items are kept as-is.",
      confirmLabel: "Restore",
    });
    if (!ok) return;
    try {
      const res = await MaintenanceAPI.reseed(selectedVehicleId);
      toast.success(res.added > 0 ? `${res.added} default item${res.added === 1 ? "" : "s"} added` : "Nothing to add");
      load();
    } catch (err) {
      logger.error("maintenance-reseed-failed", err?.message || String(err));
      toast.error("Reseed failed");
    }
  };

  // ---------- Components tab ----------
  // Separate load pipeline so switching tabs is instant and we don't waste
  // requests on the schedule tab pulling components (and vice versa).
  const loadComponents = useCallback(async () => {
    try {
      const params = selectedVehicleId !== "all" ? { vehicle_id: selectedVehicleId } : undefined;
      const list = await ComponentsAPI.list(params);
      setComponents(list);
    } catch (err) {
      logger.error("components-load-failed", err?.message || String(err));
    }
  }, [selectedVehicleId]);

  useEffect(() => {
    if (activeTab === "components") loadComponents();
  }, [activeTab, loadComponents]);

  const filteredComponents = useMemo(() => {
    if (componentStatusFilter === "all") return components;
    return components.filter((c) => c.status === componentStatusFilter);
  }, [components, componentStatusFilter]);

  const componentCounts = useMemo(() => {
    const c = { active: 0, stored: 0, archived: 0, total: components.length };
    for (const it of components) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [components]);

  const handleComponentDelete = async (component) => {
    const ok = await confirm({
      title: "Delete component?",
      description: `“${component.brand || ""} ${component.model || componentTypeMeta(component.type).label}”.trim() will be removed permanently. This can't be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await ComponentsAPI.remove(component.id);
      toast.success("Component removed");
      loadComponents();
    } catch (err) {
      logger.error("component-delete-failed", err?.message || String(err));
      toast.error("Delete failed");
    }
  };

  const handleComponentStatus = async (component, nextStatus) => {
    try {
      await ComponentsAPI.update(component.id, { status: nextStatus });
      toast.success(`Moved to ${nextStatus}`);
      loadComponents();
    } catch (err) {
      logger.error("component-status-failed", err?.message || String(err));
      toast.error("Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)]">
      <Header
        vehicles={activeVehicles}
        showFilters={false}
        onOpenSettings={() => setShowSettings(true)}
        onOpenFeedback={() => setShowFeedback(true)}
      />

      <main className="px-5 sm:px-6 lg:px-10 py-6 lg:py-8 pb-24 max-w-[1600px] mx-auto" data-testid="maintenance-main">
        {/* Hero */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6 fade-up">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1 h-3 bg-[#E82127]" />
              <span className="label-tiny">Garage</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tighter">
              Maintenance
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-2">
              Auto-generated schedule per vehicle, tuned to your drivetrain. Log services to reset the countdown.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
              <SelectTrigger
                className="w-[180px] bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] h-9"
                data-testid="maintenance-vehicle-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)]">
                <SelectItem value="all">All vehicles</SelectItem>
                {activeVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedVehicleId !== "all" && (
              <Button
                variant="outline"
                onClick={handleReseed}
                className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] h-9"
                style={{ borderRadius: 2 }}
                data-testid="maintenance-reseed-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Restore defaults
              </Button>
            )}
            <Button
              onClick={() => {
                if (activeTab === "components") { setEditingComponent(null); setShowAddComponent(true); }
                else { setEditingItem(null); setShowAdd(true); }
              }}
              disabled={activeVehicles.length === 0}
              className="bg-[#E82127] text-white hover:bg-[#FF2A30] h-9"
              style={{ borderRadius: 2 }}
              data-testid="maintenance-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
              {activeTab === "components" ? "Add component" : "Add item"}
            </Button>
          </div>
        </div>

        {/* Tab bar — Service Schedule vs Components. Underline-active
            mirrors the header nav pattern used across the app. */}
        <div className="flex items-center gap-1 border-b border-[var(--border-hover)] mb-5 fade-up" style={{ animationDelay: "0.03s" }}>
          {[
            { key: "schedule", label: "Service Schedule" },
            { key: "components", label: "Components" },
          ].map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`tap px-4 py-3 text-sm tracking-tight transition-colors relative ${
                  active ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
                data-testid={`maintenance-tab-${t.key}`}
              >
                {t.label}
                {active && <span className="absolute left-4 right-4 -bottom-px h-[2px] bg-[#E82127]" />}
              </button>
            );
          })}
        </div>

        {activeTab === "schedule" ? (
          <ScheduleTabContent
            counts={counts}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            loading={loading}
            filteredItems={filteredItems}
            vehicleById={vehicleById}
            unit={unit}
            activeVehicles={activeVehicles}
            onComplete={setCompletingItem}
            onEdit={(it) => { setEditingItem(it); setShowAdd(true); }}
            onDelete={handleDelete}
          />
        ) : (
          <ComponentsTabContent
            counts={componentCounts}
            statusFilter={componentStatusFilter}
            setStatusFilter={setComponentStatusFilter}
            components={filteredComponents}
            vehicleById={vehicleById}
            activeVehicles={activeVehicles}
            currencySymbol={currencySymbol}
            onOpen={(c) => setViewingComponent(c)}
          />
        )}
        <Footer />
      </main>

      <MaintenanceItemDialog
        open={showAdd}
        onOpenChange={(v) => { setShowAdd(v); if (!v) setEditingItem(null); }}
        vehicles={activeVehicles}
        defaultVehicleId={selectedVehicleId !== "all" ? selectedVehicleId : null}
        initialItem={editingItem}
        unit={unit}
        onSaved={() => load()}
      />
      <ComponentDialog
        open={showAddComponent}
        onOpenChange={(v) => { setShowAddComponent(v); if (!v) setEditingComponent(null); }}
        vehicles={activeVehicles}
        defaultVehicleId={selectedVehicleId !== "all" ? selectedVehicleId : null}
        initial={editingComponent}
        currencySymbol={currencySymbol}
        onSaved={(saved) => { loadComponents(); if (saved) setViewingComponent(saved); }}
      />
      <ComponentDetailSheet
        open={!!viewingComponent}
        onOpenChange={(v) => !v && setViewingComponent(null)}
        component={viewingComponent}
        vehicle={viewingComponent ? vehicleById[viewingComponent.vehicle_id] : null}
        currencySymbol={currencySymbol}
        onEdit={() => { setEditingComponent(viewingComponent); setShowAddComponent(true); setViewingComponent(null); }}
        onDelete={async () => { const c = viewingComponent; setViewingComponent(null); await handleComponentDelete(c); }}
        onStatusChange={async (nextStatus) => {
          if (!viewingComponent) return;
          await handleComponentStatus(viewingComponent, nextStatus);
          // Refresh the sheet's model so the action bar updates in place.
          try {
            const fresh = await ComponentsAPI.list({ vehicle_id: viewingComponent.vehicle_id });
            const updated = fresh.find((c) => c.id === viewingComponent.id);
            if (updated) setViewingComponent(updated); else setViewingComponent(null);
          } catch { setViewingComponent(null); }
        }}
      />
      <MaintenanceCompleteDialog
        open={!!completingItem}
        onOpenChange={(v) => !v && setCompletingItem(null)}
        item={completingItem}
        unit={unit}
        currencySymbol={currencySymbol}
        onCompleted={() => load()}
      />
      <SettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        settings={settings}
        onSave={async (data) => {
          try {
            const s = await SettingsAPI.update(data);
            setSettings(s);
            toast.success("Settings saved");
          } catch { toast.error("Failed to save"); }
        }}
        unit={unit}
        onUnitChange={setUnit}
      />
      <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />
      <ConfirmDialog />
      <WhatsNewDialog />
    </div>
  );
}

function MaintenanceRow({ item, vehicle, unit, onComplete, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const meta = statusMeta(item.status);
  const Icon = categoryIcon(item.category);
  const history = item.history || [];
  const lastServiceOdo = item.last_service_odometer_km;

  return (
    <div
      className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] group"
      style={{ borderRadius: 4, borderLeft: `2px solid ${meta.color}` }}
      data-testid={`maintenance-row-${item.id}`}
      data-status={item.status}
    >
      <div className="flex items-center gap-4 px-4 py-4">
        <div
          className="w-10 h-10 flex items-center justify-center bg-[var(--bg-elevated)] border border-[var(--border-hover)] shrink-0"
          style={{ borderRadius: 999 }}
        >
          <Icon className="w-4 h-4 text-[var(--text-primary)]" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-primary)] truncate">
              {item.name}
            </span>
            <span
              className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 font-mono-data"
              style={{
                color: meta.color,
                background: meta.ring,
                borderRadius: 2,
              }}
              data-testid={`maintenance-status-${item.id}`}
            >
              {meta.label}
            </span>
            {vehicle && (
              <span className="text-[11px] text-[var(--text-muted)] inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: vehicle.color || "#8A8A8E" }} />
                {vehicle.name}
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--text-muted)] font-mono-data truncate">
            {formatInterval(item, unit)}
            {formatRemaining(item, unit) && (
              <>
                <span className="mx-2 text-[var(--text-faint)]">·</span>
                <span
                  className={
                    item.status === "overdue"
                      ? "text-[#FF3B30]"
                      : item.status === "due_soon"
                      ? "text-[#FFB340]"
                      : "text-[var(--text-secondary)]"
                  }
                >
                  {formatRemaining(item, unit)}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onComplete}
            className="tap h-8 px-3 inline-flex items-center gap-1.5 text-xs tracking-wider uppercase bg-[var(--bg-emphasis)] hover:bg-[var(--bg-inverse)] hover:text-[var(--text-inverse)] text-[var(--text-primary)] border border-[var(--border-hover)] transition-colors"
            style={{ borderRadius: 2 }}
            data-testid={`maintenance-complete-btn-${item.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            Log
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="tap h-8 w-8 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            aria-label="Edit"
            data-testid={`maintenance-edit-btn-${item.id}`}
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="tap h-8 w-8 flex items-center justify-center text-[var(--text-muted)] hover:text-[#FF3B30]"
            aria-label="Delete"
            data-testid={`maintenance-delete-btn-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="tap h-8 w-8 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            aria-label={expanded ? "Collapse" : "Expand"}
            data-testid={`maintenance-expand-btn-${item.id}`}
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[var(--border-hover)] px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid={`maintenance-detail-${item.id}`}>
          <DetailField label="Last service">
            {item.last_service_date || "—"}
          </DetailField>
          <DetailField label={`Last odometer`}>
            {lastServiceOdo != null
              ? `${Math.round(fromKm(lastServiceOdo, unit)).toLocaleString()} ${unitLabel(unit)}`
              : "—"}
          </DetailField>
          <DetailField label="Next at">
            {item.due_at_km != null
              ? `${Math.round(fromKm(item.due_at_km, unit)).toLocaleString()} ${unitLabel(unit)}`
              : item.due_at_date || "—"}
          </DetailField>
          <DetailField label="History">
            {history.length} {history.length === 1 ? "record" : "records"}
          </DetailField>

          {history.length > 0 && (
            <div className="col-span-full">
              <div className="label-tiny mb-2">Service log</div>
              <div className="space-y-1.5">
                {history.slice().reverse().slice(0, 5).map((h) => (
                  <div
                    key={h.id || `${h.date}-${h.created_at}`}
                    className="flex items-center gap-3 text-xs px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border-hover)]"
                    style={{ borderRadius: 2 }}
                  >
                    <span className="font-mono-data text-[var(--text-primary)]">{h.date}</span>
                    {h.odometer_km != null && (
                      <span className="font-mono-data text-[var(--text-muted)]">
                        {Math.round(fromKm(h.odometer_km, unit)).toLocaleString()} {unitLabel(unit)}
                      </span>
                    )}
                    {h.cost != null && (
                      <span className="font-mono-data text-[var(--text-secondary)]">
                        ${Number(h.cost).toFixed(2)}
                      </span>
                    )}
                    {h.notes && (
                      <span className="text-[var(--text-muted)] truncate">{h.notes}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {item.notes && (
            <div className="col-span-full">
              <div className="label-tiny mb-1">Notes</div>
              <div className="text-xs text-[var(--text-secondary)] leading-relaxed">{item.notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div className="label-tiny mb-1">{label}</div>
      <div className="text-sm text-[var(--text-primary)] font-mono-data">{children}</div>
    </div>
  );
}


/* ---------------- Schedule tab content ----------------
 * Extracted so the main render stays readable when the sibling Components
 * tab renders instead. All state lives in the parent — this component is
 * a thin presentational shell over the existing filter chips + row list.
 */
function ScheduleTabContent({
  counts, statusFilter, setStatusFilter,
  loading, filteredItems, vehicleById, unit, activeVehicles,
  onComplete, onEdit, onDelete,
}) {
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6 fade-up" style={{ animationDelay: "0.05s" }}>
        {[
          { key: "all", label: "All", value: counts.total, color: "#8A8A8E" },
          { key: "overdue", label: "Overdue", value: counts.overdue, color: "#FF3B30" },
          { key: "due_soon", label: "Due Soon", value: counts.due_soon, color: "#FFB340" },
          { key: "healthy", label: "Healthy", value: counts.healthy, color: "#34D17A" },
        ].map((t) => {
          const active = statusFilter === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatusFilter(t.key)}
              className={`tap px-4 py-3 border text-left transition-colors ${
                active
                  ? "border-[var(--text-primary)] bg-[var(--bg-emphasis)]"
                  : "border-[var(--border-hover)] bg-[var(--bg-canvas)] hover:border-[var(--text-muted)]"
              }`}
              style={{ borderRadius: 2 }}
              data-testid={`maintenance-filter-${t.key}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
                <span className="label-tiny">{t.label}</span>
              </div>
              <div className="font-mono-data text-2xl leading-none mt-2 text-[var(--text-primary)]">{t.value}</div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-16 text-[var(--text-muted)] text-sm">Loading…</div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-[var(--bg-canvas)] border border-dashed border-[var(--border-hover)] p-12 flex flex-col items-center justify-center text-center" style={{ borderRadius: 4 }} data-testid="maintenance-empty">
          <div className="w-12 h-12 mb-4 flex items-center justify-center bg-[var(--bg-elevated)] border border-[var(--border-subtle)]" style={{ borderRadius: 4 }}>
            <Wrench className="w-5 h-5 text-[#E82127]" strokeWidth={1.75} />
          </div>
          <div className="label-tiny mb-2">No items</div>
          <p className="text-[var(--text-muted)] text-sm mb-6 max-w-[420px] leading-relaxed">
            {activeVehicles.length === 0
              ? "Add a vehicle to your garage first — then we'll auto-generate a service schedule tuned to its drivetrain."
              : statusFilter === "all"
              ? "Add a custom service or restore the default schedule."
              : "Nothing in this bucket right now. Nicely done."}
          </p>
        </div>
      ) : (
        <div className="space-y-2 fade-up" style={{ animationDelay: "0.1s" }}>
          {filteredItems.map((it) => (
            <MaintenanceRow
              key={it.id}
              item={it}
              vehicle={vehicleById[it.vehicle_id]}
              unit={unit}
              onComplete={() => onComplete(it)}
              onEdit={() => onEdit(it)}
              onDelete={() => onDelete(it)}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ---------------- Components tab content ----------------
 * Physical wearables (tires, brake pads, wipers, 12V battery). Layout
 * mirrors the schedule tab — same 4-chip filter bar, same row density —
 * so muscle memory carries over. Each row's right-side wear read-out is
 * driven by the type registry in `componentTypes.js`, keeping this
 * component blissfully unaware of tread depth vs pad mm vs battery %. */
function ComponentsTabContent({ counts, statusFilter, setStatusFilter, components, vehicleById, activeVehicles, currencySymbol, onOpen }) {
  const chips = [
    { key: "all", label: "All", value: counts.total, color: "#8A8A8E" },
    { key: "active", label: "Active", value: counts.active, color: "#34D17A" },
    { key: "stored", label: "Stored", value: counts.stored, color: "#FFB340" },
    { key: "archived", label: "Archived", value: counts.archived, color: "#8A8A8E" },
  ];
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6 fade-up" style={{ animationDelay: "0.05s" }}>
        {chips.map((t) => {
          const active = statusFilter === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatusFilter(t.key)}
              className={`tap px-4 py-3 border text-left transition-colors ${
                active
                  ? "border-[var(--text-primary)] bg-[var(--bg-emphasis)]"
                  : "border-[var(--border-hover)] bg-[var(--bg-canvas)] hover:border-[var(--text-muted)]"
              }`}
              style={{ borderRadius: 2 }}
              data-testid={`components-filter-${t.key}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
                <span className="label-tiny">{t.label}</span>
              </div>
              <div className="font-mono-data text-2xl leading-none mt-2 text-[var(--text-primary)]">{t.value}</div>
            </button>
          );
        })}
      </div>

      {components.length === 0 ? (
        <div className="bg-[var(--bg-canvas)] border border-dashed border-[var(--border-hover)] p-12 flex flex-col items-center justify-center text-center" style={{ borderRadius: 4 }} data-testid="components-empty">
          <div className="w-12 h-12 mb-4 flex items-center justify-center bg-[var(--bg-elevated)] border border-[var(--border-subtle)]" style={{ borderRadius: 4 }}>
            <Package className="w-5 h-5 text-[#E82127]" strokeWidth={1.75} />
          </div>
          <div className="label-tiny mb-2">No components</div>
          <p className="text-[var(--text-muted)] text-sm mb-6 max-w-[420px] leading-relaxed">
            {activeVehicles.length === 0
              ? "Add a vehicle to your garage first — components are tracked per vehicle."
              : "Track tires, brake pads, wiper blades, or the 12V battery. Add one to get started."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 fade-up" style={{ animationDelay: "0.1s" }}>
          {components.map((c) => (
            <ComponentRow
              key={c.id}
              component={c}
              vehicle={vehicleById[c.vehicle_id]}
              currencySymbol={currencySymbol}
              onOpen={() => onOpen(c)}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ---------------- Component row ----------------
 * Redesigned to match the visual language of `VehicleCard`:
 *   • 48px icon tile on the left with a red-bolt accent strip
 *   • Name + subtitle + status pill in the centre
 *   • Right-aligned primary metric + secondary label ("7 mm" / "Pad Thickness")
 *   • Full-width progress bar
 *   • Bottom row: "X km driven" · "~Y km left" (or days for wipers/battery)
 * The card is a single tap target — every lifecycle action lives inside
 * ComponentDetailSheet, keeping the row itself scannable. */
function ComponentRow({ component, vehicle, onOpen }) {
  const typeMeta = componentTypeMeta(component.type);
  const Icon = typeMeta.icon;
  const status = COMPONENT_STATUS_META[component.status] || COMPONENT_STATUS_META.active;
  const wearSum = typeMeta.summarizeWear(component.wear || {});
  const tone = typeMeta.healthTone(component.wear || {});
  const toneColor = TONE_COLORS[tone] || "#34D17A";
  const usage = usageBreakdown(component, {
    // Only feed distance to active components — stored/archived would drift
    // as the vehicle keeps driving without the part.
    distanceDrivenKm: component.status === "active" ? component.distance_driven_km : undefined,
  });
  const title = [component.brand, component.model].filter(Boolean).join(" ") || typeMeta.label;
  const subtitle = typeMeta.positions?.find((p) => p.value === component.position)?.label || typeMeta.label;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left bg-[var(--bg-canvas)] border border-[var(--border-hover)] hover:border-[#E82127]/40 transition-colors relative overflow-hidden"
      style={{ borderRadius: 4 }}
      data-testid={`component-row-${component.id}`}
      data-status={component.status}
    >
      {/* Left accent strip — coloured by health tone rather than a flat red so
          "at a glance" the row screams if something's overdue. */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px]" style={{ background: toneColor, opacity: 0.75 }} />

      <div className="p-4 sm:p-5 space-y-3">
        {/* Top row: icon + title/subtitle/status + primary metric */}
        <div className="flex items-start gap-3 sm:gap-4">
          <div
            className="w-12 h-12 shrink-0 flex items-center justify-center bg-[var(--bg-elevated)] border border-[var(--border-hover)]"
            style={{ borderRadius: 999 }}
          >
            <Icon className="w-5 h-5" strokeWidth={1.75} style={{ color: toneColor }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-display text-base font-medium tracking-tight text-[var(--text-primary)] truncate">
              {title}
            </div>
            <div className="text-xs text-[var(--text-muted)] mt-0.5 truncate">
              {subtitle}
              {vehicle && <span className="text-[var(--text-faint)]"> · {vehicle.name}</span>}
            </div>
            <span
              className="mt-2 inline-flex text-[10px] tracking-wider uppercase px-1.5 py-0.5 font-mono-data"
              style={{ color: status.color, background: status.ring, borderRadius: 2 }}
              data-testid={`component-status-${component.id}`}
            >
              {status.label}
            </span>
          </div>

          <div className="text-right shrink-0">
            <div className="font-mono-data text-lg tracking-tight text-[var(--text-primary)] leading-none" data-testid={`component-wear-display-${component.id}`}>
              {wearSum.display}
            </div>
            {typeMeta.wearLabel && (
              <div className="text-[10px] tracking-wider uppercase text-[var(--text-muted)] mt-1">
                {typeMeta.wearLabel}
              </div>
            )}
          </div>
        </div>

        {/* Progress bar — coloured by tone. Skipped when there's no signal. */}
        {typeof wearSum.pct === "number" && (
          <div className="h-1 bg-[var(--bg-elevated)] overflow-hidden" style={{ borderRadius: 2 }}>
            <div className="h-full transition-all" style={{ width: `${wearSum.pct}%`, background: toneColor }} />
          </div>
        )}

        {/* Bottom row: usage breakdown ("16,300 km driven · ~9,700 km left") */}
        {(usage.driven || usage.remaining) && (
          <div className="flex items-center justify-between text-xs font-mono-data text-[var(--text-muted)]">
            <span data-testid={`component-usage-driven-${component.id}`}>{usage.driven || "—"}</span>
            <span data-testid={`component-usage-remaining-${component.id}`}>{usage.remaining || "—"}</span>
          </div>
        )}
      </div>
    </button>
  );
}
